import {
  Command,
  TimeOffEvent,
  dayMonthDateString,
  timeStringFrom24HourTimeString,
} from '@nextforce/api'
import { Button, Text, makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import moment from 'moment'
import React from 'react'
import { View } from 'react-native'
import { CommandButtons } from 'shared/components/command_buttons'
import { BUTTON_HEIGHT, RoundedButton } from 'shared/components/rounded-button'
import { UserAvatar } from 'shared/components/user/user_avatar'

interface Props {
  timeOffEvent: TimeOffEvent
  onButtonPress: (command: Command, timeOffEvent: TimeOffEvent) => Promise<void>
}

const TIME_OFF_EVENT_LIST_CARD_HEIGHT = 220

export function TimeOffRequestCard({ timeOffEvent, onButtonPress }: Props) {
  const isPastEvent = timeOffEvent.isPastEvent
  const styles = useStyles()
  return (
    <View style={styles.card}>
      <View style={styles.avatarContainer}>
        <UserAvatar user={timeOffEvent.user} size="large" showAvatarIfNoPhone />
      </View>
      <View style={styles.descriptionContainer}>
        <View style={styles.descriptionTextContainer}>
          <View style={styles.titleRow}>
            <View style={styles.nameContainer}>
              <Text
                allowFontScaling={false}
                numberOfLines={2}
                style={[styles.name, styles.text]}
              >{`${timeOffEvent.user.firstName} ${timeOffEvent.user.lastName}`}</Text>
            </View>
            <View style={styles.creationRow}>
              <Text allowFontScaling={false} style={styles.creation}>{`${moment(
                timeOffEvent.creationDate
              ).format('DD MMM')}`}</Text>
              <Text
                allowFontScaling={false}
                style={styles.creation}
              >{`${timeStringFrom24HourTimeString(
                timeOffEvent.creationTime
              )}`}</Text>
            </View>
          </View>
          {timeOffEvent.startDate === timeOffEvent.endDate ? (
            <Text
              allowFontScaling={false}
              style={styles.text}
            >{`${dayMonthDateString(timeOffEvent.startDate)}`}</Text>
          ) : (
            <Text
              allowFontScaling={false}
              style={styles.text}
            >{`${dayMonthDateString(
              timeOffEvent.startDate
            )} - ${dayMonthDateString(timeOffEvent.endDate)}`}</Text>
          )}
          {timeOffEvent.allDay ? (
            <Text allowFontScaling={false} style={styles.text}>{`${t('days', {
              count:
                moment(timeOffEvent.endDate).diff(
                  moment(timeOffEvent.startDate),
                  'days'
                ) + 1,
            })}`}</Text>
          ) : (
            <Text
              allowFontScaling={false}
              style={styles.text}
            >{`${timeStringFrom24HourTimeString(
              timeOffEvent.startTime
            )} - ${timeStringFrom24HourTimeString(
              timeOffEvent.endTime
            )}`}</Text>
          )}
          <Text allowFontScaling={false} numberOfLines={2} style={styles.text}>
            {timeOffEvent.reason}
          </Text>
          {timeOffEvent.overlapWithBlackoutDate && (
            <Text
              allowFontScaling={false}
              numberOfLines={2}
              style={styles.alertText}
            >
              {t('pendingTimeOffRequestsOverlapWithBlackoutDatesText')}
            </Text>
          )}
        </View>
        {timeOffEvent.status !== 'PENDING' ? (
          <Button
            title={timeOffEvent.status}
            titleProps={{ allowFontScaling: false }}
            disabled={true}
            disabledStyle={styles.button}
            disabledTitleStyle={styles.title}
          />
        ) : isPastEvent ? (
          <View style={styles.buttonContainer}>
            <RoundedButton title={t('expired')} disabled onPress={() => null} />
          </View>
        ) : (
          <CommandButtons
            commands={timeOffEvent.commands}
            onButtonPress={command => onButtonPress(command, timeOffEvent)}
          />
        )}
      </View>
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  card: {
    flexDirection: 'row',
    marginHorizontal: 10,
    marginTop: 10,
    backgroundColor: theme.colors.white,
    borderRadius: 15,
    padding: 10,
    elevation: 3,
    height: TIME_OFF_EVENT_LIST_CARD_HEIGHT,
  },
  avatarContainer: { paddingVertical: 10 },
  descriptionContainer: {
    flex: 1,
    justifyContent: 'space-between',
  },
  descriptionTextContainer: {
    flex: 1,
    paddingHorizontal: 10,
  },
  text: {
    padding: 3,
    color: theme.colors.black,
  },
  nameContainer: {
    flex: 1,
  },
  name: {
    fontWeight: 'bold',
    fontSize: 16,
  },
  button: {
    height: BUTTON_HEIGHT,
    borderRadius: 15,
    backgroundColor: theme.colors.grey6,
    margin: 5,
  },
  title: {
    textAlign: 'center',
    fontSize: 17,
    fontFamily: font.bold,
    color: theme.colors?.grey2,
  },
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignContent: 'center',
  },
  creationRow: {
    flex: 1,
    flexDirection: 'row',
  },
  creation: {
    flex: 1,
    fontSize: 12,
    color: theme.colors.grey3,
    padding: 3,
  },
  alertText: {
    padding: 3,
    color: theme.colors.red,
  },
  buttonContainer: { marginVertical: 5, marginHorizontal: 10 },
}))
